<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import formValidierung from "@/businesslogic/formValidierung";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VorschadenFHVHV",
  components: {
    Zeile,
    TitelZelle,
    Zelle,
    ValidierungsFehler,
  },
  props: {
    vorschadenId: Number,
    fehler: Object,
    zeigeSchadenart: Boolean,
    sparte: String,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const vorschadenJahrMitId = ref(props.sparte + "vorschadenJahr" + props.vorschadenId);
    const vorschadenHoeheMitId = ref(props.sparte + "vorschadenHoehe" + props.vorschadenId);
    const vorschadenTypMitId = ref(props.sparte + "vorschadenTyp" + props.vorschadenId);
    const { erzwingeGanzzahl } = formValidierung();

    const jahrHeute = new Date().getFullYear();
    const jahrMinus5 = new Date().getFullYear() - 5;
    const jahrMinus10 = new Date().getFullYear() - 10;

    onMounted(() => {
      if (props.zeigeSchadenart === false && props.vorschadenId) {
        piniaStore.state.ferienhaus.vgv.vorschaeden[props.vorschadenId - 1].schadenart = defaultSchadenart();
      }
    });

    function ermittleSchadenartenAuswahl() {
      switch (piniaStore.state.verkaufsprodukt) {
        case "FH":
        case "FH2024":
          return [
            { value: "FEUER", text: "Feuer" },
            { value: "LEITUNGSWASSER", text: "Leitungswasser" },
            { value: "STURM", text: "Sturm" },
            { value: "HAGEL", text: "Hagel" },
            { value: "EINBRUCHDIEBSTAHL", text: "Einbruchdiebstahl" },
            { value: "RAUB", text: "Raub" },
            { value: "FAHRRADDIEBSTAHL", text: "Fahrraddiebstahl" },
            { value: "GLASBRUCH", text: "Glasbruch" },
            {
              value: "ELEMENTARSCHADEN_UEBERSCHWEMMUNG",
              text: "Elementarschaden(Überschwemmung/Rückstau)",
            },
            {
              value: "ELEMENTARSCHADEN_SONSTIGES",
              text: "Elementarschaden (sonstiger)",
            },
            { value: "SONSTIGER SCHADEN", text: "Sonstiger Schaden" },
          ];
      }
    }

    const schadenarten = computed(() => {
      return ermittleSchadenartenAuswahl();
    });

    const schadenJahrMin = computed(() => {
      if (
        props.vorschadenId &&
        (piniaStore.state.ferienhaus.vhv.vorschaeden[props.vorschadenId - 1].schadenart ===
          "ELEMENTARSCHADEN_UEBERSCHWEMMUNG" ||
          piniaStore.state.ferienhaus.vhv.vorschaeden[props.vorschadenId - 1].schadenart ===
            "ELEMENTARSCHADEN_SONSTIGES")
      ) {
        return jahrMinus10;
      }
      return jahrMinus5;
    });

    const defaultSchadenart = () => {
      const schadenartenAuswahl = ermittleSchadenartenAuswahl();
      if (schadenartenAuswahl) {
        return schadenartenAuswahl[0].value;
      }
      return "";
    };

    return {
      props,
      piniaStore,
      vorschadenJahrMitId,
      vorschadenHoeheMitId,
      vorschadenTypMitId,
      jahrHeute,
      schadenJahrMin,
      schadenarten,
      erzwingeGanzzahl,
    };
  },
});
</script>

<template>
  <zeile class="u-mar-t-1">
    <titel-zelle> Jahr des {{ vorschadenId }}. Schadens</titel-zelle>
    <zelle :breite="3">
      <input
        type="text"
        :name="vorschadenJahrMitId"
        v-model.lazy.trim.number="piniaStore.state.ferienhaus[sparte].vorschaeden[vorschadenId - 1].schadenjahr"
        class="c-form-control__input"
        placeholder="JJJJ"
        :min="schadenJahrMin"
        :max="jahrHeute"
        minlength="4"
        maxlength="4"
        :required="piniaStore.state.ferienhaus[sparte].tarifinformationen.vorschaedenAnzahl >= 1"
        data-checknummer="true"
        :data-fehlermeldung="
          'Bitte geben Sie das vierstellige Schadenjahr ein (' + schadenJahrMin + '-' + jahrHeute + ').'
        "
        :data-cy="'vorschadenSchadensJahr' + (vorschadenId - 1)"
      />
      <validierungs-fehler :fehler-text="fehler[vorschadenJahrMitId]" />
    </zelle>
  </zeile>
  <zeile>
    <titel-zelle>Schadenhöhe</titel-zelle>
    <zelle :breite="3">
      <input
        type="number"
        @keydown="erzwingeGanzzahl"
        :name="vorschadenHoeheMitId"
        v-model.lazy.trim.number="piniaStore.state.ferienhaus[sparte].vorschaeden[vorschadenId - 1].schadenhoehe"
        class="c-form-control__input"
        placeholder="Schadenhöhe in EUR"
        min="0"
        max="2000000"
        :required="piniaStore.state.ferienhaus[sparte].tarifinformationen.vorschaedenAnzahl >= 1"
        data-checknummer="true"
        data-fehlermeldung="Bitte geben Sie die Schadenhöhe in vollen Euro ohne Leer- und Sonderzeichen ein."
        :data-cy="'vorschadenSchadensHoehe' + (vorschadenId - 1)"
      />
      <validierungs-fehler :fehler-text="fehler[vorschadenHoeheMitId]" />
    </zelle>
  </zeile>
  <zeile v-if="zeigeSchadenart">
    <titel-zelle>Schadenart</titel-zelle>
    <zelle>
      <select
        :name="vorschadenTypMitId"
        v-model.lazy.trim="piniaStore.state.ferienhaus[sparte].vorschaeden[vorschadenId - 1].schadenart"
        class="c-form-control-select"
        :required="piniaStore.state.ferienhaus[sparte].tarifinformationen.vorschaedenAnzahl >= 1"
        :aria-required="piniaStore.state.ferienhaus[sparte].tarifinformationen.vorschaedenAnzahl >= 1"
        aria-label="Schadenart"
        data-fehlermeldung="Bitte wählen Sie eine Schadenart aus."
        :data-cy="'vorschadenSchadensArt' + (vorschadenId - 1)"
      >
        <option value="">Bitte wählen</option>
        <option v-for="item in schadenarten" :value="item.value" v-bind:key="item.value">
          {{ item.text }}
        </option>
      </select>
      <validierungs-fehler :fehler-text="fehler[vorschadenTypMitId]" :reduzierter-abstand="true" />
    </zelle>
  </zeile>
</template>
